import { HimmaContext } from '../context/HimmaContext';
import { useState, useCallback, useContext } from 'react';
import {
  AddOrEditCouponRequestSchema,
  DeleteSubAdminsRequest,
  ToggleSubAdminStatusRequest,
} from '../client-sdk';

export const useCouponApi = () => {
  const api = useContext(HimmaContext)!;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create Coupon
  const createCoupon = useCallback(
    async (couponData: AddOrEditCouponRequestSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.addCoupon(couponData);
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to create user: No response data');
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'An error occurred while creating user';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Edit Customer
  const editCouponById = useCallback(
    async (couponId: any, customerData: AddOrEditCouponRequestSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.editCoupon(couponId, customerData);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // List Coupon
  const getAllCoupon = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        // Only include search parameter if it has a value
        const searchParam =
          query.search && query.search.trim() !== '' ? query.search : undefined;
        const response = await api.getAllCoupons(
          query.page,
          query.limit,
          query.sortBy,
          query.sortOrder,
          searchParam
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Delete Coupon
  const deleteCouponById = useCallback(
    async (query: string[]) => {
      setLoading(true);
      setError(null);
      try {
        const deleteCoupon: DeleteSubAdminsRequest = {
          ids: query,
        };
        const response = await api.deleteCoupons(deleteCoupon);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );
  // Block/Unblock Coupon
  const blockOrUnblockCouponById = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        const toggleSubAdminStatusRequest: ToggleSubAdminStatusRequest = {
          ids: Array.isArray(query.id) ? query.id : [query.id],
          status: query.status,
        };
        const response = await api.toggleCouponStatus(
          toggleSubAdminStatusRequest
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const generateCoupon = async () => {
    try {
      const response = await api.generateCouponCode();
      return response.data;
    } catch (err) {
      throw err instanceof Error ? err : new Error('An error occurred');
    }
  };

  return {
    createCoupon,
    loading,
    error,
    getAllCoupon,
    deleteCouponById,
    blockOrUnblockCouponById,
    editCouponById,
    generateCoupon,
  };
};
